/**
 * Ticker — configurable-height bottom strip for a standing message (#212).
 *
 * Renders the per-screen ticker config delivered in the schedule manifest
 * (e.g. "SCAN ITEM TO RECYCLE"). Four scroll styles:
 *  - rtl / ltr: horizontal marquee. The text is duplicated and the track
 *    animated by exactly half its width, so the loop is seamless.
 *  - up / down: vertical crawl — the line slides through the strip.
 *
 * Animations are pure CSS (keyframes injected once) so the ticker costs no
 * JS per frame on the Pi. Colors/fonts come inline from the config.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ScheduleManifest } from '@dooh/shared';

export type TickerConfig = NonNullable<ScheduleManifest['ticker']>;

/** Default strip height when the config omits one (older manifests). */
export const DEFAULT_TICKER_HEIGHT_PX = 90;

const FONT_STACKS: Record<TickerConfig['font'], string> = {
  sans: 'ui-sans-serif, system-ui, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  mono: 'ui-monospace, "DejaVu Sans Mono", monospace',
  condensed: '"Barlow Condensed", "Arial Narrow", sans-serif',
  // Heavy display options. The Pi image has no Arial Black/Impact, so the
  // stacks fall back to the system sans rendered at the mapped weight below —
  // still visibly thicker than the regular options.
  black: '"Archivo Black", "Arial Black", sans-serif',
  impact: '"Anton", Impact, Haettenschweiler, sans-serif',
};

/** Weight per font: heavy options render at 900 even via fallback fonts. */
const FONT_WEIGHTS: Record<TickerConfig['font'], number> = {
  sans: 600,
  serif: 600,
  mono: 600,
  condensed: 600,
  black: 900,
  impact: 400, // Anton is a single heavy weight; 400 avoids fake-bold smearing
};

/** How long the vertical crawl holds the text centered each pass. */
export const VERTICAL_PAUSE_S = 5;

/** Inject the horizontal marquee keyframes once per document. */
function ensureHorizontalKeyframes() {
  const id = 'dooh-ticker-keyframes';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = `
@keyframes dooh-ticker-rtl { from { transform: translateX(0); } to { transform: translateX(-50%); } }
@keyframes dooh-ticker-ltr { from { transform: translateX(-50%); } to { transform: translateX(0); } }
`;
  document.head.appendChild(style);
}

/**
 * Vertical crawl with a centered hold: slide in, pause VERTICAL_PAUSE_S in
 * the middle of the strip, slide out. The hold is expressed as two identical
 * keyframe stops, so the timing lives in the percentages — which depend on
 * the travel:pause ratio, hence one injected keyframe set per (direction,
 * ratio) pair.
 */
function ensureVerticalKeyframes(direction: 'up' | 'down', holdStartPct: number, holdEndPct: number): string {
  const name = `dooh-tkv-${direction}-${Math.round(holdStartPct * 10)}`;
  const id = `keyframes-${name}`;
  if (!document.getElementById(id)) {
    const fromY = direction === 'up' ? '100%' : '-100%';
    const toY = direction === 'up' ? '-100%' : '100%';
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
@keyframes ${name} {
  0% { transform: translateY(${fromY}); }
  ${holdStartPct}% { transform: translateY(0); }
  ${holdEndPct}% { transform: translateY(0); }
  100% { transform: translateY(${toY}); }
}
`;
    document.head.appendChild(style);
  }
  return name;
}

export interface TickerProps {
  config: TickerConfig;
}

export function Ticker({ config }: TickerProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(0);

  const height = config.height && config.height >= 30 ? config.height : DEFAULT_TICKER_HEIGHT_PX;
  const horizontal = config.direction === 'rtl' || config.direction === 'ltr';

  useEffect(() => {
    if (horizontal) ensureHorizontalKeyframes();
  }, [horizontal]);

  // Measure the rendered text so horizontal speed (px/s) maps to a duration.
  useLayoutEffect(() => {
    if (trackRef.current) {
      setTrackWidth(trackRef.current.scrollWidth);
    }
  }, [config.text, config.font]);

  const speed = config.speed > 0 ? config.speed : 60;

  // Horizontal: the track holds 2 copies and moves half its width per loop.
  const durationS = horizontal ? Math.max(1, trackWidth / 2 / speed) : 0;

  // Vertical: travel one strip-height per leg at `speed`, hold centered for
  // VERTICAL_PAUSE_S between the legs.
  const travelS = Math.max(0.4, height / speed);
  const cycleS = travelS * 2 + VERTICAL_PAUSE_S;
  const holdStartPct = Math.round((travelS / cycleS) * 1000) / 10;
  const holdEndPct = Math.round(((travelS + VERTICAL_PAUSE_S) / cycleS) * 1000) / 10;
  const verticalName = !horizontal
    ? ensureVerticalKeyframes(config.direction as 'up' | 'down', holdStartPct, holdEndPct)
    : '';

  // Text scales with the strip so 90px doesn't render 14px type.
  const fontSizePx = Math.round(height * 0.45);

  const commonText = (
    <span
      className="px-8 uppercase tracking-wider"
      style={{
        color: config.textColor,
        fontFamily: FONT_STACKS[config.font],
        fontWeight: FONT_WEIGHTS[config.font],
        fontSize: fontSizePx,
      }}
    >
      {config.text}
    </span>
  );

  return (
    <div
      data-testid="dooh-ticker"
      className="w-full shrink-0 overflow-hidden"
      style={{ height, backgroundColor: config.bgColor }}
    >
      {horizontal ? (
        <div
          ref={trackRef}
          className="flex h-full w-max items-center whitespace-nowrap"
          style={{
            animation: `dooh-ticker-${config.direction} ${durationS}s linear infinite`,
          }}
        >
          {commonText}
          {commonText}
        </div>
      ) : (
        <div className="relative h-full w-full overflow-hidden">
          <div
            className="absolute inset-0 flex items-center justify-center whitespace-nowrap"
            style={{
              animation: `${verticalName} ${cycleS}s linear infinite`,
            }}
          >
            {commonText}
          </div>
        </div>
      )}
    </div>
  );
}
